import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryCostingPolicy } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-costing-policies',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, SelectModule, CheckboxModule, AinCanAccessDirective],
  template: `
    <ain-page [title]="t('catalog.costing.policies.title')" [description]="t('catalog.costing.policies.description')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.policies.help.title'"
        [descriptionKey]="'catalog.costing.policies.help.description'"
        [bulletKeys]="['catalog.costing.policies.help.bullets.scope', 'catalog.costing.policies.help.bullets.specificity']" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.states.noPermissionMessage')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'" [label]="t('catalog.costing.policies.bootstrapDefault')" (click)="bootstrapDefault()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>

          <details class="filters" [open]="showFilters()">
            <summary>{{ t('catalog.filter.show') }}</summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.inventory.offeringId') }}</span>
                <p-select [options]="offeringOptions()" [(ngModel)]="filters.offeringId" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" (ngModelChange)="load()" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.warehouseId') }}</span>
                <p-select [options]="warehouseOptions()" [(ngModel)]="filters.warehouseId" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
              </label>
              <label class="check">
                <p-checkbox [binary]="true" [(ngModel)]="filters.activeOnly" (ngModelChange)="load()" />
                <span>{{ t('catalog.costing.policies.activeOnly') }}</span>
              </label>
            </div>
          </details>

          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.costing.policies.empty')" />
          } @else {
            <p-table [value]="rows()" [paginator]="true" [rows]="15" dataKey="id">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.costing.policies.method') }}</th>
                  <th>{{ t('catalog.inventory.offeringId') }}</th>
                  <th>{{ t('catalog.inventory.warehouseId') }}</th>
                  <th>{{ t('columns.currency') }}</th>
                  <th>{{ t('catalog.costing.policies.effectiveFrom') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ methodLabel(row.method) }}</button></td>
                  <td>{{ resolveOffering(row.offeringId) }}</td>
                  <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                  <td>{{ row.currencyCode }}</td>
                  <td>{{ row.effectiveFromUtc }}</td>
                  <td>{{ row.isActive ? t('catalog.costing.policies.active') : t('catalog.costing.policies.inactive') }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (error()) { <p class="error">{{ error() }}</p> }
          @if (bootstrapMessage()) { <p class="success">{{ bootstrapMessage() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; }
    .toolbar { display: flex; gap: 8px; margin-bottom: 10px; }
    .filters { margin-bottom: 10px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .check { display: flex; align-items: center; gap: 8px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    .success { color: #15803d; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryCostingPoliciesComponent implements OnInit {
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InventoryCostingPolicy[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly showFilters = signal(true);
  readonly bootstrapMessage = signal('');

  filters = { offeringId: '' as string, warehouseId: '' as string, activeOnly: true };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.costingApi.listPolicies({
      offeringId: this.filters.offeringId || undefined,
      warehouseId: this.filters.warehouseId || undefined,
      isActive: this.filters.activeOnly ? true : undefined
    }).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('catalog.costing.states.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/inventory/costing/policies/new']);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/inventory/costing/policies', id]);
  }

  bootstrapDefault(): void {
    this.bootstrapMessage.set('');
    this.error.set('');
    this.costingApi.bootstrapDefaultPolicy().subscribe({
      next: result => {
        this.bootstrapMessage.set(result.created
          ? this.t('catalog.costing.policies.bootstrapCreated')
          : this.t('catalog.costing.policies.bootstrapExists'));
        this.load(false);
      },
      error: () => this.error.set(this.t('catalog.costing.policies.bootstrapFailed'))
    });
  }

  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));

  resolveOffering(id: string | null): string {
    if (!id) return this.t('catalog.costing.policies.tenantDefault');
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : '-';
  }

  resolveWarehouse(id: string | null): string {
    if (!id) return this.t('catalog.costing.policies.allWarehouses');
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : '-';
  }

  methodLabel(method: string): string {
    const key = `catalog.costing.methods.${method}`;
    const translated = this.t(key);
    return translated === key ? method : translated;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
